<template>
  <v-main>
    <v-container fluid>
      <!-- Requests waiting on the admin -->
      <div v-if="pendingCount > 0" class="rp-banner mb-3">
        <span class="rp-banner__icon"><v-icon color="white" small>mdi-swap-horizontal</v-icon></span>
        <span class="rp-banner__text">
          <b>{{ pendingCount }}</b> replacement request{{ pendingCount === 1 ? " is" : "s are" }} waiting for approval.
        </span>
        <v-spacer></v-spacer>
        <v-btn text small color="orange darken-3" @click="showPending">View requests</v-btn>
      </div>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-swap-horizontal"
                title="Replacements"
                subtitle="Same product, same or another size - no time limit (the apps show the button for 7 days after delivery)"
                style="width: 100%"
              >
                <v-tooltip bottom v-if="can('REPLACEMENTS.CREATE')">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" fab x-small color="primary" @click="openCreate()">
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                  <span>New Replacement</span>
                </v-tooltip>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search RP #, Order # or Customer... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-filter-variant"
                    clearable
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
                <v-col sm="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.source"
                    :items="sourceOptions"
                    item-text="text"
                    item-value="value"
                    label="Raised From"
                    prepend-inner-icon="mdi-cellphone"
                    clearable
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="500" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">RP #</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Order #</th>
                      <th class="text-left">Customer</th>
                      <th class="text-left">Swap</th>
                      <th class="text-left">Raised From</th>
                      <th class="text-right">Extra</th>
                      <th class="text-left">Replacement Order</th>
                      <th class="text-left">Status</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="rows.length != 0">
                    <tr v-for="(rp, i) in rows" :key="`rp_${rp.id}`" class="clickable-row" @click="view(rp)">
                      <td style="min-width: 85px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td>
                        {{ rp.replacement_number }}
                        <v-chip v-if="rp.window_overridden" x-small color="orange" text-color="white" class="ml-1">after 48h</v-chip>
                      </td>
                      <td>{{ rp.created_at | niceDate }}</td>
                      <td>{{ rp.order ? rp.order.order_no : "" }}</td>
                      <td>
                        <div>{{ userName(rp.customer) }}</div>
                        <div class="text-caption grey--text" v-if="rp.customer">{{ rp.customer.phone }}</div>
                      </td>
                      <td class="rp-swap-cell">
                        <div v-for="item in rp.items" :key="`s_${item.id}`" class="text-caption">
                          {{ item.quantity }} × {{ productName(item.old_variant) }}:
                          <b>{{ variantLabel(item.old_variant) }}</b>
                          <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                          <b>{{ variantLabel(item.new_variant) }}</b>
                        </div>
                      </td>
                      <td>{{ sourceLabels[rp.source] }}</td>
                      <td class="text-right mono-figure">
                        <span v-if="Number(rp.extra_amount) > 0">₹{{ money(rp.extra_amount) }}</span>
                        <span v-else class="grey--text">&mdash;</span>
                      </td>
                      <td>
                        <span v-if="rp.new_order">{{ rp.new_order.order_no }}</span>
                        <span v-else class="grey--text">&mdash;</span>
                      </td>
                      <td>
                        <v-chip small :color="statusColors[rp.status]" text-color="white">{{ statusLabels[rp.status] }}</v-chip>
                      </td>
                      <td class="text-right action_th">
                        <v-btn fab x-small class="my-auto" :color="rp.status === 0 ? 'orange' : 'primary'" @click.stop="view(rp)">
                          <v-icon dark>{{ rp.status === 0 ? "mdi-gavel" : "mdi-eye" }}</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="11">
                        <AppEmptyState
                          icon="mdi-swap-horizontal"
                          title="No replacements yet"
                          message="Replacement requests from customers and counter swaps by sellers show up here."
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- View / decide -->
    <AppDialog
      :show.sync="showView"
      max-width="860"
      icon="mdi-swap-horizontal"
      :title="viewing ? viewing.replacement_number : ''"
      :subtitle="viewing && viewing.order ? viewing.order.order_no : ''"
      :status-text="viewing ? statusLabels[viewing.status] : ''"
      :status-color="viewing ? dialogStatusColors[viewing.status] : 'grey'"
    >
      <div v-if="viewing" class="rp-view">
        <div class="rp-facts">
          <div class="rp-fact">
            <v-icon class="rp-fact__icon">mdi-account-outline</v-icon>
            <div>
              <div class="rp-fact__label">Customer</div>
              <div class="rp-fact__value">{{ userName(viewing.customer) || "-" }}</div>
              <div class="rp-fact__sub" v-if="viewing.customer">{{ viewing.customer.phone }}</div>
            </div>
          </div>
          <div class="rp-fact">
            <v-icon class="rp-fact__icon">{{ sourceIcons[viewing.source] }}</v-icon>
            <div>
              <div class="rp-fact__label">Raised from</div>
              <div class="rp-fact__value">{{ sourceLabels[viewing.source] }}</div>
              <div class="rp-fact__sub">by {{ userName(viewing.requested_by) }}</div>
            </div>
          </div>
          <div class="rp-fact">
            <v-icon class="rp-fact__icon">mdi-truck-check-outline</v-icon>
            <div>
              <div class="rp-fact__label">Delivered on</div>
              <div class="rp-fact__value">{{ viewing.order && viewing.order.delivered_at ? $options.filters.niceDate(viewing.order.delivered_at) : "-" }}</div>
              <div class="rp-fact__sub">Requested {{ viewing.created_at | niceDate }}</div>
            </div>
          </div>
          <div class="rp-fact">
            <v-icon class="rp-fact__icon">mdi-cash-plus</v-icon>
            <div>
              <div class="rp-fact__label">Customer pays</div>
              <div class="rp-fact__value mono-figure">{{ Number(viewing.extra_amount) > 0 ? "₹" + money(viewing.extra_amount) : "Nothing" }}</div>
              <div class="rp-fact__sub">{{ Number(viewing.extra_amount) > 0 ? "size difference" : "same price or cheaper" }}</div>
            </div>
          </div>
        </div>

        <div v-if="viewing.window_overridden" class="rp-note mb-4">
          <v-icon small color="orange darken-2" class="mr-2">mdi-clock-alert-outline</v-icon>
          Raised after the 48 hour window (admin override).
        </div>

        <div class="rp-reason">
          <div class="rp-section-label">Reason</div>
          <div class="rp-reason__text">“{{ viewing.reason || "No reason given" }}”</div>
        </div>

        <div class="rp-section-label">Swap</div>
        <div class="rp-items">
          <div v-for="item in viewing.items" :key="`vi_${item.id}`" class="rp-item">
            <div class="rp-item__side">
              <div class="rp-item__thumb">
                <img v-if="variantImage(item.old_variant)" :src="variantImage(item.old_variant)" alt="" />
                <v-icon v-else color="grey lighten-1">mdi-tshirt-crew-outline</v-icon>
              </div>
              <div>
                <div class="rp-item__name">{{ productName(item.old_variant) }}</div>
                <div class="rp-item__meta">Has: {{ variantLabel(item.old_variant) }} · ₹{{ money(item.old_unit_price) }}</div>
              </div>
            </div>
            <v-icon class="rp-item__arrow">mdi-arrow-right-thick</v-icon>
            <div class="rp-item__side">
              <div class="rp-item__thumb">
                <img v-if="variantImage(item.new_variant)" :src="variantImage(item.new_variant)" alt="" />
                <v-icon v-else color="grey lighten-1">mdi-tshirt-crew-outline</v-icon>
              </div>
              <div>
                <div class="rp-item__name">{{ variantLabel(item.new_variant) }}</div>
                <div class="rp-item__meta">Gets · ₹{{ money(item.new_unit_price) }}</div>
              </div>
            </div>
            <div class="rp-item__qty mono-figure">
              × {{ item.quantity }}
              <v-chip v-if="item.damaged_quantity > 0" x-small color="red lighten-5" text-color="red darken-2" class="ml-1">
                {{ item.damaged_quantity }} back damaged · held
              </v-chip>
            </div>
            <div class="rp-item__extra mono-figure">
              <span v-if="Number(item.extra_amount) > 0">+₹{{ money(item.extra_amount) }}</span>
              <span v-else class="grey--text">₹0</span>
            </div>
          </div>
        </div>

        <!-- Outcome -->
        <div v-if="viewing.status !== 0" class="rp-outcome" :class="`rp-outcome--${viewing.status}`">
          <v-icon class="rp-outcome__icon">{{ outcomeIcons[viewing.status] }}</v-icon>
          <div>
            <div class="rp-outcome__title">{{ outcomeTitle(viewing) }}</div>
            <div class="rp-outcome__meta" v-if="viewing.processed_by">
              By {{ userName(viewing.processed_by) }}<span v-if="viewing.approved_at"> · {{ viewing.approved_at | niceDateTime }}</span>
            </div>
            <div v-if="viewing.admin_remarks" class="mt-1">{{ viewing.admin_remarks }}</div>
          </div>
        </div>

        <!-- Pending: decide -->
        <div v-else-if="can('REPLACEMENTS.UPDATE')" class="rp-decision">
          <div class="rp-decision__title">Your decision</div>
          <div class="rp-decision__hint">
            Approving takes the new items out of stock and creates a replacement order for delivery
            <span v-if="Number(viewing.extra_amount) > 0">- the customer pays ₹{{ money(viewing.extra_amount) }} on delivery</span>.
            The items they hand back go into stock when it's delivered - damaged ones are held in Return Stock instead.
          </div>
          <div class="rp-section-label mt-3">Condition of the items they hand back</div>
          <div v-for="line in decisionItems" :key="`dc_${line.id}`" class="d-flex align-center mb-2" style="gap: 12px">
            <div style="min-width: 220px">{{ line.label }}</div>
            <v-text-field
              v-model.number="line.damaged_quantity"
              type="number"
              min="0"
              :max="line.quantity"
              outlined
              dense
              hide-details
              suffix="damaged"
              style="max-width: 150px"
            ></v-text-field>
            <span class="text-caption grey--text">of {{ line.quantity }} · the rest go back to stock</span>
          </div>
          <v-textarea
            v-model="remarks"
            outlined
            dense
            rows="2"
            auto-grow
            class="mt-3"
            placeholder="Remarks - required if you reject (the customer sees it in the app)"
            hide-details="auto"
            :error-messages="remarksError"
          ></v-textarea>
        </div>
      </div>
      <template v-slot:actions v-if="viewing && viewing.status === 0 && can('REPLACEMENTS.UPDATE')">
        <v-btn color="success" depressed :loading="deciding === 'approve'" :disabled="!!deciding" @click="approve">
          <v-icon left>mdi-check</v-icon> Approve &amp; create order
        </v-btn>
        <v-btn color="error" outlined class="ml-2" :loading="deciding === 'reject'" :disabled="!!deciding" @click="reject">
          <v-icon left>mdi-close</v-icon> Reject
        </v-btn>
      </template>
    </AppDialog>

    <!-- New replacement (admin, at the counter) -->
    <AppDialog
      :show.sync="showCreate"
      persistent
      max-width="960"
      icon="mdi-swap-horizontal"
      title="New Replacement"
      subtitle="Swap items on a delivered order - done straight away"
    >
      <v-autocomplete
        v-model="createForm.order_id"
        :items="orderOptions"
        :search-input.sync="orderSearch"
        :loading="isSearchingOrders"
        item-value="id"
        :item-text="orderLabel"
        no-filter
        outlined
        dense
        hide-no-data
        prepend-inner-icon="mdi-magnify"
        label="Find a delivered order by order # or customer name/phone"
        @change="loadOptions"
      ></v-autocomplete>

      <section v-if="isLoadingOptions" class="py-6">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </section>
      <div v-else-if="options">
        <div v-if="!options.can_replace" class="rp-note mb-3">
          <v-icon small color="orange darken-2" class="mr-2">mdi-information-outline</v-icon>
          {{ options.message }}
        </div>

        <template v-if="options.can_replace">
          <div class="rp-section-label">
            Items
          </div>
          <div class="rp-items mb-4">
            <div v-for="line in lines" :key="`ln_${line.id}`" class="rp-line">
              <div class="rp-line__info">
                <div class="rp-item__name">{{ line.product_name }}</div>
                <div class="rp-item__meta">Has: {{ [line.color, line.size].filter(Boolean).join(" - ") }} · bought {{ line.quantity }}<span v-if="line.replaced_quantity"> · {{ line.replaced_quantity }} already replaced</span></div>
              </div>
              <v-select
                v-model="line.new_variant_id"
                :items="line.options"
                item-value="id"
                :item-text="optionLabel"
                :item-disabled="(o) => o.in_stock <= 0"
                outlined
                dense
                hide-details
                label="Replace with"
                class="rp-line__select"
                :disabled="line.replaceable_quantity <= 0"
              ></v-select>
              <v-text-field
                v-model.number="line.qty"
                type="number"
                min="0"
                :max="line.replaceable_quantity"
                outlined
                dense
                hide-details="auto"
                label="Qty"
                class="rp-line__qty"
                :disabled="line.replaceable_quantity <= 0"
                :error-messages="line.qty > line.replaceable_quantity ? `Max ${line.replaceable_quantity}` : ''"
              ></v-text-field>
              <!-- Of the pieces handed back, how many are damaged (held in Return Stock, not resold) -->
              <v-text-field
                v-model.number="line.damaged_qty"
                type="number"
                min="0"
                :max="Number(line.qty) || 0"
                outlined
                dense
                hide-details="auto"
                label="Damaged"
                class="rp-line__qty"
                :disabled="!(Number(line.qty) > 0)"
                :error-messages="line.damaged_qty > (Number(line.qty) || 0) ? `Max ${Number(line.qty) || 0}` : ''"
                @input="line.damaged_touched = true"
              ></v-text-field>
              <div class="rp-item__extra mono-figure">
                <span v-if="lineExtra(line) > 0">+₹{{ money(lineExtra(line)) }}</span>
                <span v-else class="grey--text">₹0</span>
              </div>
            </div>
          </div>

          <v-row dense>
            <v-col cols="12">
              <v-textarea v-model="createForm.reason" outlined dense rows="2" auto-grow hide-details="auto">
                <template v-slot:label>Reason <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-textarea>
            </v-col>
            <v-col cols="12" md="6" v-if="createExtra > 0">
              <div class="rp-section-label mt-3">Customer pays ₹{{ money(createExtra) }} now via</div>
              <v-btn-toggle v-model="createForm.extra_payment_mode" mandatory class="rp-modes" active-class="rp-modes__active">
                <v-btn :value="0" depressed><v-icon left small>mdi-cash</v-icon> Cash</v-btn>
                <v-btn :value="1" depressed><v-icon left small>mdi-qrcode-scan</v-icon> Online</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </template>
      </div>

      <template v-slot:actions>
        <v-btn color="primary" depressed :loading="isCreating" :disabled="!canCreate" @click="createReplacement">
          Replace{{ createExtra > 0 ? ` & collect ₹${money(createExtra)}` : "" }}
        </v-btn>
      </template>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";

export default {
  name: "replacementsPage",
  components: { AppDialog, AppEmptyState, AppPageHeader },
  data() {
    return {
      filter: { search_keyword: null, status: null, source: null },
      page: 1,
      paginationLength: 0,
      count: 0,
      pendingCount: 0,
      rows: [],
      isLoading: false,
      statusOptions: [
        { text: "Requested", value: 0 },
        { text: "Approved (on its way)", value: 1 },
        { text: "Rejected", value: 2 },
        { text: "Completed", value: 3 },
        { text: "Cancelled", value: 4 },
      ],
      sourceOptions: [
        { text: "Customer App", value: 0 },
        { text: "Sales App", value: 1 },
        { text: "Admin Panel", value: 2 },
      ],
      statusLabels: { 0: "Requested", 1: "On its way", 2: "Rejected", 3: "Completed", 4: "Cancelled" },
      statusColors: { 0: "orange", 1: "blue", 2: "error", 3: "success", 4: "grey" },
      dialogStatusColors: { 0: "warning", 1: "warning", 2: "error", 3: "success", 4: "error" },
      outcomeIcons: { 1: "mdi-truck-fast-outline", 2: "mdi-close-circle", 3: "mdi-check-circle", 4: "mdi-cancel" },
      sourceLabels: { 0: "Customer App", 1: "Sales App", 2: "Admin Panel" },
      sourceIcons: { 0: "mdi-cellphone", 1: "mdi-storefront-outline", 2: "mdi-monitor" },
      // view
      showView: false,
      viewing: null,
      remarks: "",
      remarksError: "",
      decisionItems: [], // condition of the pieces handed back, per line (approve)
      deciding: null,
      // create
      showCreate: false,
      createForm: {},
      orderSearch: null,
      orderOptions: [],
      isSearchingOrders: false,
      searchTimer: null,
      options: null,
      lines: [],
      isLoadingOptions: false,
      isCreating: false,
    };
  },
  computed: {
    createExtra() {
      return this.lines.reduce((sum, line) => sum + this.lineExtra(line), 0);
    },
    canCreate() {
      if (!this.options || !this.options.can_replace) return false;
      const chosen = this.lines.filter((l) => Number(l.qty) > 0);
      return (
        chosen.length > 0 &&
        chosen.every((l) => l.new_variant_id && Number(l.qty) <= l.replaceable_quantity) &&
        !!(this.createForm.reason || "").trim()
      );
    },
  },
  watch: {
    page: "getData",
    orderSearch(value) {
      clearTimeout(this.searchTimer);
      if (!value || value.length < 2) return;
      this.searchTimer = setTimeout(() => this.searchOrders(value), 350);
    },
  },
  mounted() {
    this.getData();
    // Coming from an order's "Replace Items" action
    if (this.$route.query.order_id) {
      this.openCreate(Number(this.$route.query.order_id));
    }
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    userName(user) {
      if (!user) return "";
      return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.user_name || "";
    },
    productName(variant) {
      return variant && variant.product ? variant.product.name : "";
    },
    variantLabel(variant) {
      if (!variant) return "";
      return [variant.color, variant.size].filter(Boolean).join(" - ");
    },
    variantImage(variant) {
      const images = (variant && variant.product_variant_images) || [];
      return images.length && images[0].image_path ? this.mediaUrl + images[0].image_path : null;
    },
    orderLabel(order) {
      const customer = this.userName(order.customer);
      return `${order.order_no}${customer ? " - " + customer : ""} (₹${order.final_amount || order.total_amount})`;
    },
    optionLabel(o) {
      const name = [o.color, o.size].filter(Boolean).join(" - ");
      const extra = o.extra_per_unit > 0 ? ` (+₹${this.money(o.extra_per_unit)})` : "";
      const stock = o.in_stock <= 0 ? " - out of stock" : "";
      return `${name}${o.is_current ? " (same)" : ""}${extra}${stock}`;
    },
    lineExtra(line) {
      const option = (line.options || []).find((o) => o.id === line.new_variant_id);
      return option ? option.extra_per_unit * (Number(line.qty) || 0) : 0;
    },
    outcomeTitle(rp) {
      if (rp.status === 1) return `Approved - replacement order ${rp.new_order ? rp.new_order.order_no : ""} is on its way`;
      if (rp.status === 3) return `Completed - replacement delivered${rp.new_order ? " (" + rp.new_order.order_no + ")" : ""}`;
      if (rp.status === 4) return "Cancelled - the replacement order was cancelled";
      return "Replacement request rejected";
    },
    showPending() {
      this.filter.status = 0;
      this.page = 1;
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status === null || this.filter.status === undefined ? "" : this.filter.status,
        source: this.filter.source === null || this.filter.source === undefined ? "" : this.filter.source,
      });
      await axios
        .get(`replacements?${params.toString()}`)
        .then((response) => {
          this.rows = response.data.data;
          this.count = response.data.count;
          this.pendingCount = response.data.pending_count || 0;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch((error) => console.log(error));
      this.isLoading = false;
    },
    view(rp) {
      this.viewing = rp;
      this.remarks = "";
      this.remarksError = "";
      // Starts from the request (a "damaged / defective" reason marks every piece damaged)
      this.decisionItems = (rp.items || []).map((i) => ({
        id: i.id,
        quantity: i.quantity,
        damaged_quantity: i.damaged_quantity || 0,
        label: `${this.productName(i.old_variant)} - ${this.variantLabel(i.old_variant)}`,
      }));
      this.showView = true;
    },
    async approve() {
      this.deciding = "approve";
      await axios
        .post(`replacements/${this.viewing.id}/approve`, {
          admin_remarks: this.remarks || null,
          items: this.decisionItems.map((l) => ({
            id: l.id,
            damaged_quantity: Math.max(0, Math.min(l.quantity, Number(l.damaged_quantity) || 0)),
          })),
        })
        .then((response) => {
          this.notify("success", response.data.message);
          this.viewing = response.data.data;
          this.getData();
        })
        .catch((error) => this.notify("error", (error.response && error.response.data.message) || "Could not approve."));
      this.deciding = null;
    },
    async reject() {
      if (!(this.remarks || "").trim()) {
        this.remarksError = "Tell the customer why the replacement is rejected.";
        return;
      }
      this.deciding = "reject";
      await axios
        .post(`replacements/${this.viewing.id}/reject`, { admin_remarks: this.remarks })
        .then((response) => {
          this.notify("success", response.data.message);
          this.viewing = response.data.data;
          this.getData();
        })
        .catch((error) => this.notify("error", (error.response && error.response.data.message) || "Could not reject."));
      this.deciding = null;
    },
    openCreate(orderId = null) {
      this.createForm = { order_id: null, reason: "", extra_payment_mode: 0 };
      this.options = null;
      this.lines = [];
      this.orderOptions = [];
      this.showCreate = true;
      if (orderId) {
        this.createForm.order_id = orderId;
        this.loadOptions();
      }
    },
    async searchOrders(keyword) {
      this.isSearchingOrders = true;
      await axios
        .get(`orders?order_status=4&page=1&rowsPerPage=20&search_keyword=${encodeURIComponent(keyword)}`)
        .then((response) => (this.orderOptions = response.data.data || []))
        .catch((error) => console.log(error));
      this.isSearchingOrders = false;
    },
    async loadOptions() {
      this.options = null;
      this.lines = [];
      if (!this.createForm.order_id) return;
      this.isLoadingOptions = true;
      await axios
        .get(`replacements/order/${this.createForm.order_id}`)
        .then((response) => {
          this.options = response.data;
          const data = response.data.data;
          // Default: replace 1 of each with the same size (the damaged-item case)
          this.lines = data.lines.map((l) => ({
            ...l,
            qty: l.replaceable_quantity > 0 ? 1 : 0,
            new_variant_id: l.variant_id,
            damaged_qty: 0,
            damaged_touched: false,
          }));
          if (!this.orderOptions.find((o) => o.id === data.order_id)) {
            this.orderOptions = [{ id: data.order_id, order_no: data.order_no }];
          }
        })
        .catch((error) => this.notify("error", (error.response && error.response.data.message) || "Could not load the order."));
      this.isLoadingOptions = false;
    },
    async createReplacement() {
      this.isCreating = true;
      const payload = {
        order_id: this.createForm.order_id,
        reason: this.createForm.reason,
        extra_payment_mode: this.createExtra > 0 ? this.createForm.extra_payment_mode : null,
        items: this.lines
          .filter((l) => Number(l.qty) > 0)
          .map((l) => {
            const line = { order_detail_id: l.id, quantity: Number(l.qty), new_variant_id: l.new_variant_id };
            // Untouched: the API decides from the reason ("damaged" / "defective" = all damaged)
            if (l.damaged_touched || Number(l.damaged_qty) > 0) {
              line.damaged_quantity = Math.max(0, Math.min(line.quantity, Number(l.damaged_qty) || 0));
            }
            return line;
          }),
      };
      await axios
        .post(`replacements`, payload)
        .then((response) => {
          this.showCreate = false;
          this.notify("success", response.data.message);
          this.page = 1;
          this.getData();
        })
        .catch((error) => this.notify("error", (error.response && error.response.data.message) || "Could not create the replacement."));
      this.isCreating = false;
    },
    notify(type, message) {
      if (this.$toast) this.$toast[type](message);
    },
  },
};
</script>

<style scoped>
.rp-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fae8dd;
}
.rp-banner__icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #ffa24b;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rp-banner__text {
  color: #f08a24;
  font-weight: 500;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover > td {
  background-color: #fbf7ef !important;
}
.rp-swap-cell {
  min-width: 240px;
}

.rp-view {
  color: var(--color-ink);
}
.rp-section-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(46, 42, 36, 0.6);
  margin-bottom: 8px;
}
.rp-facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #ece5da;
  border-radius: 12px;
  background: #fbf9f5;
  margin-bottom: 20px;
}
.rp-fact {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  min-width: 0;
}
.rp-fact + .rp-fact {
  border-left: 1px solid #ece5da;
}
.rp-fact__icon {
  color: var(--color-primary-dark) !important;
  font-size: 20px !important;
}
.rp-fact__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(46, 42, 36, 0.5);
}
.rp-fact__value {
  margin-top: 2px;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rp-fact__sub {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.6);
}
.rp-note {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(224, 164, 88, 0.12);
  color: #8a5a1f;
  font-size: 13px;
}
.rp-reason {
  margin-bottom: 20px;
}
.rp-reason__text {
  padding: 12px 16px;
  border-left: 3px solid var(--color-primary);
  border-radius: 0 8px 8px 0;
  background: rgba(202, 181, 150, 0.12);
  font-style: italic;
}
.rp-items {
  border: 1px solid #ece5da;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}
.rp-item,
.rp-line {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
}
.rp-item + .rp-item,
.rp-line + .rp-line {
  border-top: 1px solid #f1ece4;
}
.rp-item__side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.rp-item__thumb {
  flex: 0 0 40px;
  height: 48px;
  border-radius: 8px;
  background: #f3efe8;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.rp-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rp-item__arrow {
  color: var(--color-primary-dark) !important;
}
.rp-item__name {
  font-weight: 600;
}
.rp-item__meta {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.6);
}
.rp-item__qty {
  color: rgba(46, 42, 36, 0.65);
  white-space: nowrap;
}
.rp-item__extra {
  min-width: 90px;
  text-align: right;
  font-weight: 600;
}
.rp-line__info {
  flex: 1 1 auto;
  min-width: 0;
}
.rp-line__select {
  flex: 0 0 260px;
}
.rp-line__qty {
  flex: 0 0 90px;
}
.rp-outcome {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(110, 143, 163, 0.12);
}
.rp-outcome--3 {
  background: rgba(76, 154, 106, 0.1);
}
.rp-outcome--3 .rp-outcome__icon {
  color: var(--color-success) !important;
}
.rp-outcome--2,
.rp-outcome--4 {
  background: rgba(216, 86, 74, 0.08);
}
.rp-outcome--2 .rp-outcome__icon,
.rp-outcome--4 .rp-outcome__icon {
  color: var(--color-error) !important;
}
.rp-outcome__title {
  font-weight: 600;
}
.rp-outcome__meta {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.6);
}
.rp-decision {
  padding: 18px 20px;
  border: 1px solid #ece5da;
  border-radius: 12px;
}
.rp-decision__title {
  font-size: 16px;
  font-weight: 700;
}
.rp-decision__hint {
  font-size: 13px;
  color: rgba(46, 42, 36, 0.65);
}
.rp-modes {
  display: flex;
  width: 100%;
}
.rp-modes .v-btn {
  flex: 1 1 0;
  height: 40px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  background: #fff !important;
}
.rp-modes .rp-modes__active {
  background: rgba(138, 111, 78, 0.12) !important;
  color: var(--color-primary-dark) !important;
}
@media (max-width: 760px) {
  .rp-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .rp-line,
  .rp-item {
    flex-wrap: wrap;
  }
  .rp-line__select {
    flex-basis: 100%;
  }
}
</style>
